export interface IDataApp {
  authenticated: boolean
  activeToastSignOut: boolean
  toastSignOutMessage: string
  info: string
}

export interface Item {
  storeName: string;
  sug: boolean;
  name: string;
  price: number;
  oldPrice?: number;
  description: string;
  discount: boolean;
  discountAmount?: number;
}

export interface CartProduct {
  item: Item;
  qty: number;
  obs: string;
}

export interface CartProductItem {
  item: Item;
  qty: number;
  obs: string;
  index: number;
}

export interface Cart {
  items: CartProduct[];
}

export interface PropsModal {
  item: Item;
  modalShow: boolean;
}

export interface BooleanPropsModal {
  showModal: boolean;
}

export interface Option {
  name: string;
  value: number;
}

export interface Customer {
  id?: string;
  name: string;
  email: string;
  cpf?: string;
  phoneDdd: string;
  phoneNumber: string;
  password?: string;
  confirmPassword?: string;
  avatarPath?: string;
}

export interface CustomerAddress {
  id?: string;
  name: string;
  street: string;
  streetNumber: string;
  complement?: string;
  neighborhood: string;
  city: string;
  cityCode?: string;
  state: string;
  postalCode: string;
  reference?: string;
  errors?: any;
  country?: string;
  latitude?: number;
  longitude?: number;
}

export interface CustomerFavorite {
  id: string;
  productId: string;
  productName: string;
  productDescription: string;
  productImagePath: string;
  merchantId: string;
  merchantUrlCode: string;
  merchantName: string;
  merchantLogoPath: string;
  merchantMarketSegmentName: string;
  productAvailability: string;
}

export interface City {
  city: string;
  cityCode: string;
  state: string;
}

export interface Action {
  payload: any;
  type: string;
  [key: string]: any;
}

export enum OrderStatus {
  AWAITING = 'AWAITING',
  INTEGRATED = 'INTEGRATED',
  CONFIRMED = 'CONFIRMED',
  READY = 'READY',
  DISPATCHED = 'DISPATCHED',
  CANCELED = 'CANCELED',
  CONCLUDED = 'CONCLUDED',
  INPROGRESS = 'INPROGRESS',
  DRIVETHRU = 'DRIVETHRU',
  AWAITING_PAYMENT = 'AWAITING_PAYMENT',
}
export enum OrderStatusTranslation {
  AWAITING = 'Pendente',
  INTEGRATED = 'Pendente',
  CONFIRMED = 'Em Preparo',
  READY = 'Aguardando Retirada',
  DISPATCHED = 'Em Trânsito',
  CANCELED = 'Cancelado',
  CONCLUDED = 'Concluído',
  INPROGRESS = 'INPROGRESS',
  DRIVETHRU = 'Drive Thru',
  AWAITING_PAYMENT = 'Aguardando',
}
export enum MerchantStatus {
  AVAILABLE = 'AVAILABLE',
  UNAVAILABLE = 'UNAVAILABLE',
}
export enum DeliveryMethod {
  DELIVERY = 'DELIVERY',
  TAKEOUT = 'TAKEOUT',
  DRIVETHRU = 'DRIVETHRU',
  SHIP = 'SHIP',
}
export enum DeliveryMethodTranslation {
  DELIVERY = 'DELIVERY',
  TAKEOUT = 'RETIRADA NO ESTABELECIMENTO',
  DRIVETHRU = 'DRIVETHRU',
  SHIP = 'TRANSPORTADORA',
}

export enum StatusDeliveryShip {
  PENDING = 'PENDING',
  RELEASED = 'RELEASED',
  POSTED = 'POSTED',
  DELIVERED = 'DELIVERED',
  CANCELED = 'CANCELED',
  UNDELIVERED = 'UNDELIVERED',
  SUSPENDED = 'SUSPENDED'
}
export enum StatusDeliveryShipTranslation {
  PENDING = 'Aguardando Despacho',
  RELEASED = 'Lançado',
  POSTED = 'Postado',
  DELIVERED = 'Entregue',
  CANCELED = 'Cacelado',
  UNDELIVERED = 'Não entregue',
  SUSPENDED = 'Suspenso'
}

export enum Month {
  janeiro,
  fevereiro,
  março,
  abril,
  maio,
  junho,
  julho,
  agosto,
  setembro,
  outubro,
  novembro,
  dezembro,
}
export interface CustomerOrder {
  id: string;
  shortId: string;
  status: OrderStatus;
  total: number;
  createdAt: string;
  cancellationReason?: string;
  merchantId: string;
  merchantUrlCode: string;
  merchantStatus: MerchantStatus;
  merchantName: string;
  merchantLogoPath: string;
  rating: number;
  deliveryMethod: DeliveryMethod;
}

/**
 * @method : Delivery method
 * @address : Customer Address
 */
export interface Delivery {
  method: DeliveryMethod;
  address: Address;
  ship: DeliveryShip;
  time?: number;
}
/**
 * @street : name of street
 * @streetNumber : number of address
 * @complement : an complement
 * @neighborhood : name of neighborhood
 * @city : name of city
 * @cityCode : city code
 * @state : name of state
 * @country : name of state
 * @postalCode : cep number
 * @reference : reference
 * @latitude : latitude
 * @longitude : longitude
 */

export interface Address {
  street: string;
  streetNumber: string;
  complement: string;
  neighborhood: string;
  city: string;
  cityCode: string;
  state: string;
  country: string;
  postalCode: string;
  reference: string;
  latitude: number;
  longitude: number;
}

export interface Variation {
  variationId: string;
  variationName: string;
  variationItemId: string;
  variationItemName: string;
  quantity: number;
  value: number;
  total: number;
  variationsGrid: boolean;
}

/**
 * @item : name of item
 * @productId : id of item
 * @quantity : quantity of item
 * @price : price of item
 * @subtotal : subtotal of item
 * @discount : discount of item price
 * @addition : additional of item price
 * @total : total value of item
 * @observations : observations of item
 */
export interface Product {
  item: number;
  productId: string;
  productCode: string;
  productName: string;
  quantity: number;
  price: number;
  subtotal: number;
  discount: number;
  addition: number;
  total: number;
  observations: string;
  variationsTotal: number;
  variations: Variation[];
  variationsGrid: boolean;
}

/**
 * @cardNumber : number of card
 * @expiryDate : expiration date of card
 * @securityCode : CVV
 * @holder : card owner
 * @cpf : CPF of card owner
 * @installments : number of value parts
 */

export interface CreditCard {
  cardNumber: string;
  expiryDate: string;
  securityCode: string;
  holder: string;
  cpf: string;
  installments: number;
}

/**
 * @code : code of payment, ex: "DIN" for money
 * @prepaid : online payment ? true
 * @value : value of payment
 * @changeFor : change of payment using DIN, value
 * @creditCard : credit card infos
 */

export interface Payment {
  code: string;
  prepaid: boolean;
  value: number;
  changeFor: number;
  creditCard?: CreditCard;
}

export interface DeliveryShip {
  companyName: string;
  labelTracking: string;
  labelUrl: string;
  nfeKey: string;
  nfeNumber: string;
  serviceName: string;
  status: StatusDeliveryShip;
}

export interface OrderResponse {
  merchantId: string;
  id: string;
  shortId: string;
  status: OrderStatus;
  customer: Customer;
  delivery: Delivery;
  preparationTime: number;
  observation: string;
  items: Product[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  payments: PaymentOrder[];
  createdAt: string;
  canceledAt: string;
  confirmedAt: string;
  dispatchedAt: string;
  readyAt: string;
  updatedAt: string;
  cancellation: {
    origin: string;
    reason: string;
  };
  rating: Rating;
}

/**
 * State type
 * @data
 */
export interface OrderState {
  merchantId: string;
  customer: Customer;
  delivery: Delivery;
  observation: string;
  items: Product[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  payments: Payment[];
}

export interface Rating {
  rating: number;
  comment: string;
  createdAt: string;
}
export interface PaymentOrder {
  code: string;
  description: string;
  prepaid: boolean;
  value: number;
  changeFor: number;
  installments: number;
  cardTransaction: {
    authorizerMessage: string;
    customerReceipt: string;
    merchantReceipt: string;
    authorizerId: string;
    acquirerId: string;
    acquirerName: string;
    authorizerDate: string;
    authorizationNumber: string;
    esitefUsn: string;
    sitefUsn: string;
    hostUsn: string;
    paymentType: string;
    issuer: string;
    isCanceled: boolean;
    cancellationCustomerReceipt: string;
    cancellationMerchantReceipt: string;
  };
  pixTransaction: {
    status: string;
    amount: number;
    dueDate: Date;
    qrCodeImage: string;
    qrCodeLink: string;
    paidReceipt: string;
    paidAt: Date;
    refundReceipt: string;
    refundAt: Date;
  };
}

export interface Segment {
  code: string;
  iconPath: string;
  name: string;
}

export interface Avatar {
  path: string;
}
